import { useCallback, useEffect, useState } from "react";

const BROWSE_SEEN_KEY = "browseTabSeen";
const BROWSE_SEEN_EVENT = "browse-tab-seen";

/** Whether the user has opened Browse before, persisted in localStorage. */
const readSeen = () => {
  try {
    return localStorage.getItem(BROWSE_SEEN_KEY) === "1";
  } catch {
    return false;
  }
};

/**
 * Tracks whether the user has ever opened Browse. `seen` flips to true after
 * `markSeen()` and stays true across sessions; both the bottom bar and the
 * desktop rail share the state via a window event so a click on one surface
 * updates the other in the same session.
 *
 * @returns {[boolean, () => void]} `[seen, markSeen]`
 */
export const useBrowseSeen = () => {
  const [seen, setSeen] = useState(readSeen);

  useEffect(() => {
    const sync = () => setSeen(readSeen());
    window.addEventListener(BROWSE_SEEN_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(BROWSE_SEEN_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const markSeen = useCallback(() => {
    try {
      localStorage.setItem(BROWSE_SEEN_KEY, "1");
    } catch {
      // ignore write failures (private mode / disabled storage)
    }
    setSeen(true);
    window.dispatchEvent(new Event(BROWSE_SEEN_EVENT));
  }, []);

  return [seen, markSeen];
};
